import { CollectionTable, CollectionCards } from '../../app/collection-view.js';
import {
  Button,
  Combobox,
  Input,
  Modal,
  MonthPicker,
  SegmentedControl,
  StatusPill,
  StatusText,
  formatCivilDate,
  formatCurrency,
  useSnackbar,
  type ComboboxChoice,
  type StatusTone,
  IconButton,
} from '@hue/design-system';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronRight, Inbox, Check, ShieldQuestion, CalendarDays, Undo2 } from 'lucide-react';
import { Fragment, useState, type FormEvent, type ReactNode } from 'react';
import {
  EmptyCard,
  ErrorCard,
  FilterBar,
  FilterGroup,
  LoadingCard,
  Pagination,
  RequestAlert,
  SortBar,
  SortableColumn,
  SummaryStrip,
} from '../../app/data-view.js';
import { useClosedPeriod } from '../../app/closed-period-dialog.js';
import type { ClassificationView } from '../../app/list-views.js';
import { useOperationalPeriod } from '../../app/operational-period-context.js';
import { PageHeader } from '../../app/page-title.js';
import type { ViewSetter } from '../../app/view-state.js';
import { getApiError, getApiErrorField } from '../../lib/api.js';
import { accountingApi, type AccountingAccount, type QueueRow } from './api.js';
import {
  DIFFERENCE_REASONS,
  PART_LABELS,
  effectOf,
  isContrary,
  monthName,
  originOf,
  postableAccounts,
  reasonSuggestion,
  ruleNoteOf,
} from './classification-texts.js';

const KINDS = [
  { value: 'MOVEMENTS', label: 'Extrato' },
  { value: 'OBLIGATIONS', label: 'Compromissos' },
  { value: 'RECURRENCES', label: 'Recorrências' },
] as const;

const FILTERS = [
  { value: 'PENDING', label: 'Sem conta' },
  { value: 'CLASSIFIED', label: 'Classificados' },
  { value: 'ALL', label: 'Todos' },
] as const;

const PART_TONES: Readonly<Record<QueueRow['part'], StatusTone>> = {
  WAITING: 'warning',
  DECLARED: 'neutral',
  DIFFERENCE: 'brand',
  OBLIGATION: 'neutral',
  RECURRENCE: 'neutral',
};

const keyOf = (row: Pick<QueueRow, 'subjectKind' | 'subjectKey'>) =>
  `${row.subjectKind}:${row.subjectKey}`;

/// Quanto a gaveta das parcelas leva para abrir ou fechar.
const GAVETA_MS = 200;

/// As colunas da tabela, repetidas na grade das parcelas para elas ficarem alinhadas embaixo.
const PARCEL_COLUMNS =
  'grid-cols-[2.5rem_minmax(0,9fr)_minmax(0,29fr)_minmax(0,13fr)_minmax(0,11fr)_minmax(0,24fr)_minmax(0,14fr)]';

/// Cada parcela segue sendo um compromisso: a linha da compra só junta a ação.
const targetsOf = (rows: readonly QueueRow[]) => rows.flatMap((row) => row.parcels ?? [row]);

/// "Persiana · parcela 1/6" → "Persiana". O agrupamento é pela compra na origem; aqui é só o nome.
const PARCEL = /\s*·\s*parcela\s+(\d+)\/(\d+)\s*$/u;
const purchaseName = (description: string) => description.replace(PARCEL, '');
/// Dentro da compra, a parcela se chama pelo número: o nome da compra já está na linha de cima.
const parcelName = (description: string) => {
  const match = PARCEL.exec(description);
  return match ? `Parcela ${match[1]} de ${match[2]}` : description;
};

const percent = (ratio: number | null) => (ratio === null ? '—' : `${Math.floor(ratio * 100)}%`);

type Declaring = {
  row: QueueRow;
  reason: string;
  month: string;
  accountId: string | null;
  errors: { reason?: string | undefined; form?: string | undefined };
};

export function ClassificationQueue({
  view,
  onView,
  today,
  canDeclare,
}: {
  view: ClassificationView;
  onView: ViewSetter<ClassificationView>;
  today: string;
  /// Declarar sem compromisso é do Financeiro: exige a permissão de conciliar e uma unidade
  /// escolhida, porque grava um fato do extrato.
  canDeclare: boolean;
}) {
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const closedPeriod = useClosedPeriod();
  const period = useOperationalPeriod(today);
  const from = period.from ?? `${today.slice(0, 7)}-01`;
  const to = period.to ?? today;
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [declaring, setDeclaring] = useState<Declaring | null>(null);
  const [competence, setCompetence] = useState<{ row: QueueRow; month: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  /// A gaveta existe enquanto está aberta ou fechando. Fechada, não sobra linha nenhuma na tabela.
  const [mounted, setMounted] = useState<Set<string>>(new Set());
  /// Parcelas que já têm outra conta: trocar a conta delas nunca acontece calado.
  const [confirming, setConfirming] = useState<{
    rows: QueueRow[];
    account: AccountingAccount | NonNullable<QueueRow['account']>;
    changes: QueueRow[];
  } | null>(null);

  const accounts = useQuery({
    queryKey: ['company', 'accounting', 'accounts'],
    queryFn: accountingApi.accounts,
  });
  const page = useQuery({
    queryKey: ['company', 'accounting', 'queue', view, from, to],
    queryFn: () =>
      accountingApi.queue({
        kind: view.kind,
        filter: view.filter,
        sort: view.sort,
        direction: view.direction,
        limit: view.limit,
        offset: view.offset,
        from,
        to,
        ...(view.search.trim() ? { search: view.search.trim() } : {}),
      }),
    placeholderData: keepPreviousData,
  });

  const plan = accounts.data ?? [];
  const postable = postableAccounts(plan);
  const options = (direction: 'INFLOW' | 'OUTFLOW') =>
    postable.map((account) => ({
      value: account.id,
      label: `${account.code} ${account.name}`,
      ...(isContrary(account.code, direction) ? { description: 'Sentido contrário' } : {}),
    }));

  async function refresh() {
    setSelected(new Set());
    await queryClient.invalidateQueries({ queryKey: ['company', 'accounting'] });
  }

  async function classify(
    rows: QueueRow[],
    account: AccountingAccount | QueueRow['account'] | undefined,
    confirmed = false,
  ) {
    if (!account) return;
    const changes = rows
      .flatMap((row) => row.parcels ?? [])
      .filter((parcel) => parcel.account && parcel.account.id !== account.id);
    if (changes.length > 0 && !confirmed) {
      setConfirming({ rows, account, changes });
      return;
    }
    setConfirming(null);
    setBusy(true);
    try {
      const targets = targetsOf(rows);
      const keys = targets.map((row) => ({ kind: row.subjectKind, key: row.subjectKey }));
      try {
        await accountingApi.classify(keys, account.id);
      } catch (error) {
        if (
          closedPeriod.intercept(
            error,
            (override) => accountingApi.classify(keys, account.id, override),
            refresh,
          )
        )
          return;
        throw error;
      }
      await refresh();
      const only = rows.length === 1 ? rows[0] : undefined;
      snackbar.success(
        only?.parcels
          ? `${targets.length} parcelas classificadas em ${account.code} ${account.name}.`
          : only?.part === 'RECURRENCE'
            ? `Recorrência classificada. Os próximos meses gerados já entram em ${account.code} ${account.name}.`
            : only?.part === 'WAITING'
              ? `Conta ${account.code} ${account.name} escolhida. Por enquanto vale só no Fluxo de caixa.`
              : rows.length > 1
                ? `${rows.length} lançamentos classificados em ${account.code} ${account.name}.`
                : `Classificado em ${account.code} ${account.name}.`,
      );
    } catch (error) {
      snackbar.failure(getApiError(error));
    } finally {
      setBusy(false);
    }
  }

  /// Tirar a conta devolve a linha a Sem conta. A compra tira a de todas as parcelas.
  async function clear(rows: QueueRow[]) {
    const targets = targetsOf(rows).filter((row) => row.account);
    if (targets.length === 0) return;
    setBusy(true);
    try {
      const keys = targets.map((row) => ({ kind: row.subjectKind, key: row.subjectKey }));
      try {
        await accountingApi.clear(keys);
      } catch (error) {
        if (
          closedPeriod.intercept(error, (override) => accountingApi.clear(keys, override), refresh)
        )
          return;
        throw error;
      }
      await refresh();
      snackbar.success(
        targets.length === 1
          ? 'Conta tirada. A linha voltou para Sem conta.'
          : `Conta tirada de ${targets.length} lançamentos. Eles voltaram para Sem conta.`,
      );
    } catch (error) {
      snackbar.failure(getApiError(error));
    } finally {
      setBusy(false);
    }
  }

  const pick =
    (rows: QueueRow[]) =>
    (choice: ComboboxChoice): void => {
      if (choice.kind !== 'option') return;
      void classify(
        rows,
        plan.find((account) => account.id === choice.value),
      );
    };

  async function undeclare(row: QueueRow) {
    setBusy(true);
    try {
      await accountingApi.undeclare(row.subjectKey);
      await refresh();
      snackbar.success(
        'Declaração desfeita. O movimento volta a aguardar conciliação e sai do DRE; a conta continua guardada.',
      );
    } catch (error) {
      snackbar.failure(getApiError(error));
    } finally {
      setBusy(false);
    }
  }

  async function submitDeclaration(event: FormEvent) {
    event.preventDefault();
    if (!declaring) return;
    if (declaring.reason.trim().length < 3) {
      setDeclaring({
        ...declaring,
        errors: { reason: 'Diga por que este dinheiro nunca vai pagar um compromisso.' },
      });
      return;
    }
    setBusy(true);
    const declaration = {
      movementId: declaring.row.subjectKey,
      reason: declaring.reason.trim(),
      competenceMonth: declaring.month,
      accountId: declaring.accountId,
    };
    try {
      try {
        await accountingApi.declare(declaration);
      } catch (error) {
        if (
          closedPeriod.intercept(
            error,
            (override) => accountingApi.declare(declaration, override),
            async () => {
              setDeclaring(null);
              await refresh();
            },
          )
        )
          return;
        throw error;
      }
      setDeclaring(null);
      await refresh();
      snackbar.success(
        declaring.accountId
          ? `Declarado sem compromisso. Passa a valer no DRE de ${monthName(declaring.month)}.`
          : `Declarado sem compromisso. Entra no DRE de ${monthName(declaring.month)} na linha “Sem conta” até ganhar conta.`,
      );
    } catch (error) {
      setDeclaring({
        ...declaring,
        errors:
          getApiErrorField(error) === 'reason'
            ? { reason: getApiError(error) }
            : { form: getApiError(error) },
      });
    } finally {
      setBusy(false);
    }
  }

  async function submitCompetence(event: FormEvent) {
    event.preventDefault();
    if (!competence) return;
    setBusy(true);
    const change = competence;
    try {
      try {
        await accountingApi.setCompetence(
          change.row.subjectKind,
          change.row.subjectKey,
          change.month,
        );
      } catch (error) {
        if (
          closedPeriod.intercept(
            error,
            (override) =>
              accountingApi.setCompetence(
                change.row.subjectKind,
                change.row.subjectKey,
                change.month,
                override,
              ),
            async () => {
              setCompetence(null);
              await refresh();
            },
          )
        )
          return;
        throw error;
      }
      setCompetence(null);
      await refresh();
      snackbar.success(`Competência alterada para ${monthName(competence.month)}.`);
    } catch (error) {
      snackbar.failure(getApiError(error));
    } finally {
      setBusy(false);
    }
  }

  const rows = page.data?.rows ?? [];
  const summary = page.data?.summary;
  const total = page.data?.total ?? 0;
  const selectedRows = rows.filter((row) => selected.has(keyOf(row)));
  const toggle = (row: QueueRow) => {
    const next = new Set(selected);
    if (next.has(keyOf(row))) next.delete(keyOf(row));
    else next.add(keyOf(row));
    setSelected(next);
  };
  const toggleSort = (sort: ClassificationView['sort']) =>
    onView({
      sort,
      direction: view.sort === sort && view.direction === 'asc' ? 'desc' : 'asc',
    });
  const columns = [
    { sort: 'date' as const, label: view.kind === 'OBLIGATIONS' ? 'Competência' : 'Data' },
    { sort: 'description' as const, label: 'Lançamento' },
    { sort: 'amount' as const, label: 'Valor' },
  ];

  const summaryItems =
    view.kind === 'MOVEMENTS'
      ? [
          {
            label: 'Partes sem conta',
            value: `${summary?.pendingCount ?? 0} · ${formatCurrency(summary?.pendingCents ?? 0, 'BRL')}`,
            tone: 'warning' as const,
          },
          {
            label: 'Aguardando conciliação',
            value: formatCurrency(summary?.waitingCents ?? 0, 'BRL'),
          },
          { label: 'Classificado do caixa', value: percent(summary?.classifiedRatio ?? null) },
        ]
      : view.kind === 'OBLIGATIONS'
        ? [
            {
              label: 'Compromissos sem conta',
              value: `${summary?.pendingCount ?? 0} · ${formatCurrency(summary?.pendingCents ?? 0, 'BRL')}`,
              tone: 'warning' as const,
            },
            {
              label: 'Classificado da competência',
              value: percent(summary?.classifiedRatio ?? null),
            },
          ]
        : [
            {
              label: 'Recorrências sem conta',
              value: `${summary?.pendingCount ?? 0}`,
              tone: 'warning' as const,
            },
            { label: 'O que isto mede', value: 'Meses futuros' },
          ];

  const accountField = (row: QueueRow) => {
    const suggestion = row.suggestion ?? reasonSuggestion(row, plan);
    return (
      <div className="grid min-w-0 gap-1.5">
        <Combobox
          label={
            row.parcels
              ? `Conta de todas as parcelas de ${purchaseName(row.description)}`
              : `Conta de ${row.description}`
          }
          hideLabel
          placeholder={row.mixed ? 'Classificação mista' : 'Escolher conta'}
          selectedLabel={row.account ? `${row.account.code} ${row.account.name}` : ''}
          options={options(row.direction)}
          clearable={targetsOf([row]).some((one) => one.account !== null)}
          onClear={() => void clear([row])}
          disabled={busy}
          onSelect={pick([row])}
          emptyLabel="Nenhuma conta ativa com esse nome"
        />
        {suggestion ? (
          <div className="flex flex-wrap items-center gap-2 text-xs text-brand-400">
            <span>
              {row.suggestion ? 'Sugestão do extrato conciliado' : 'Sugestão pelo motivo'}:{' '}
              {suggestion.code} {suggestion.name}
            </span>
            <IconButton
              label="Usar"
              icon={<Check />}
              onClick={() => void classify([row], suggestion)}
            />
          </div>
        ) : null}
      </div>
    );
  };

  /// A situação divide a célula com texto de tabela: mesmo corpo do resto da linha (text-sm).
  const statusOf = (row: QueueRow) =>
    row.mixed ? (
      <div className="grid gap-1 text-sm">
        <StatusText tone="warning">Classificação mista</StatusText>
        <span className="text-xs text-content-muted">
          {row.parcels?.filter((parcel) => !parcel.account).length
            ? 'Há parcelas sem conta.'
            : 'As parcelas estão em contas diferentes.'}
        </span>
      </div>
    ) : row.account ? (
      <div className="grid gap-1 text-sm">
        <StatusText tone="positive">{originOf(row)}</StatusText>
        {row.classifiedAt ? (
          <span className="text-xs text-content-muted">
            {formatCivilDate(row.classifiedAt.slice(0, 10))}
          </span>
        ) : null}
        {ruleNoteOf(row) ? (
          <span className="text-xs text-content-muted">{ruleNoteOf(row)}</span>
        ) : null}
        {isContrary(row.account.code, row.direction) ? (
          <span className="text-xs text-content-muted">
            Sentido contrário: {row.direction === 'INFLOW' ? 'entrada' : 'saída'} nesta conta{' '}
            {row.direction === 'INFLOW' ? 'reduz a despesa' : 'reduz a receita'}.
          </span>
        ) : null}
      </div>
    ) : (
      <div className="grid gap-1 text-sm">
        <StatusText tone="warning">Sem conta</StatusText>
        {ruleNoteOf(row) ? (
          <span className="text-xs text-content-muted">{ruleNoteOf(row)}</span>
        ) : null}
      </div>
    );

  const actionsOf = (row: QueueRow) => {
    if (row.parcels) return null;
    if (row.part === 'WAITING') {
      if (row.linked) {
        return (
          <span className="text-xs text-content-muted">
            Concilie o resto ou resolva a diferença na Conciliação.
          </span>
        );
      }
      return canDeclare ? (
        <IconButton
          label="Declarar sem compromisso"
          icon={<ShieldQuestion />}
          disabled={busy}
          onClick={() =>
            setDeclaring({
              row,
              reason: '',
              month: (row.date ?? today).slice(0, 7),
              accountId: row.account?.id ?? null,
              errors: {},
            })
          }
        />
      ) : null;
    }
    if (row.part === 'DECLARED' || row.part === 'DIFFERENCE') {
      return (
        <div className="flex flex-wrap gap-1">
          <IconButton
            label="Alterar competência"
            icon={<CalendarDays />}
            disabled={busy}
            onClick={() => setCompetence({ row, month: row.competenceMonth ?? today.slice(0, 7) })}
          />
          {row.part === 'DECLARED' && canDeclare ? (
            <IconButton
              label="Desfazer declaração"
              icon={<Undo2 />}
              disabled={busy}
              onClick={() => void undeclare(row)}
            />
          ) : null}
        </div>
      );
    }
    return null;
  };

  const toggleParcels = (row: QueueRow) => {
    const key = keyOf(row);
    const change = (set: Set<string>, add: boolean) => {
      const next = new Set(set);
      if (add) next.add(key);
      else next.delete(key);
      return next;
    };
    if (expanded.has(key)) {
      // Fecha animando e só depois tira a gaveta da tabela.
      setExpanded((current) => change(current, false));
      window.setTimeout(() => setMounted((current) => change(current, false)), GAVETA_MS);
      return;
    }
    // Monta fechada e abre no quadro seguinte, para a altura ter de onde crescer.
    setMounted((current) => change(current, true));
    window.requestAnimationFrame(() => setExpanded((current) => change(current, true)));
  };

  /// Abre e fecha como gaveta: a altura vai de zero ao conteúdo e volta, nos dois sentidos. As
  /// parcelas ficam montadas; fechadas, saem do alcance do teclado e do leitor de tela (`inert`).
  const drawer = (open: boolean, content: ReactNode, padding = 'px-5 py-3') => (
    <div
      inert={!open}
      className={`grid transition-[grid-template-rows,opacity] duration-200 ease-out motion-reduce:transition-none ${
        open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
      }`}
    >
      <div className="min-h-0 overflow-hidden">
        <div className={padding}>{content}</div>
      </div>
    </div>
  );

  const isOpen = (row: QueueRow) => expanded.has(keyOf(row));

  const describe = (row: QueueRow) => (
    <div className="min-w-0">
      {row.parcels ? (
        <button
          type="button"
          aria-expanded={isOpen(row)}
          aria-label={
            isOpen(row) ? 'Esconder as parcelas' : `Ver as ${row.parcels.length} parcelas`
          }
          onClick={() => toggleParcels(row)}
          className="-ml-1 flex cursor-pointer items-start gap-1 rounded-lg px-1 text-left text-sm font-semibold text-content hover:text-brand-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
        >
          <ChevronRight
            aria-hidden="true"
            className={`mt-0.5 size-4 shrink-0 text-brand-400 transition-transform motion-reduce:transition-none ${
              isOpen(row) ? 'rotate-90' : ''
            }`}
          />
          <span className="break-words">{purchaseName(row.description)}</span>
        </button>
      ) : (
        <p className="break-words text-sm font-semibold text-content">{row.description}</p>
      )}
      <p className="break-words text-xs text-content-muted">
        {row.detail}
        {row.parcels ? ` · ${row.parcels.length} parcelas` : ''}
        {row.movementCents !== null && row.movementCents !== row.amountCents
          ? ` · parte de um movimento de ${formatCurrency(row.movementCents, 'BRL')}`
          : ''}
      </p>
      <p className="mt-1 max-w-md text-xs text-content-muted">{effectOf(row)}</p>
    </div>
  );

  /// A parcela aberta debaixo da compra: recuada, ligada por um fio à linha de cima e chamada pelo
  /// número, para ler como parte da compra e não como outro lançamento.
  const describeParcel = (parcel: QueueRow) => (
    <div className="ml-2 min-w-0 border-l-2 border-brand-500/40 pl-4">
      <p className="text-sm text-content">{parcelName(parcel.description)}</p>
      <p className="text-xs text-content-muted">
        {parcel.date ? `Competência ${formatCivilDate(parcel.date)}` : ''}
      </p>
    </div>
  );

  const partOf = (row: QueueRow) => (
    <div className="grid gap-1">
      <StatusPill tone={PART_TONES[row.part]}>{PART_LABELS[row.part]}</StatusPill>
      {row.reason ? (
        <span className="text-xs text-content-muted">
          {DIFFERENCE_REASONS[row.reason] ?? row.reason}
        </span>
      ) : null}
    </div>
  );

  const amountOf = (row: QueueRow) =>
    row.amountCents === null ? (
      <span className="text-sm text-content-muted">Valor variável</span>
    ) : (
      <span
        className={`text-sm font-semibold tabular-nums ${row.direction === 'INFLOW' ? 'text-positive' : ''}`}
      >
        {row.direction === 'INFLOW' ? '+' : '−'} {formatCurrency(row.amountCents, 'BRL')}
      </span>
    );

  const checkbox = (row: QueueRow) => (
    <input
      type="checkbox"
      className="hue-checkbox size-4 cursor-pointer accent-brand-500"
      checked={selected.has(keyOf(row))}
      onChange={() => toggle(row)}
      aria-label={`Selecionar ${row.description}`}
    />
  );

  const emptyText = view.search.trim()
    ? `Nenhum lançamento com “${view.search.trim()}” neste filtro.`
    : view.filter === 'PENDING'
      ? 'Isso quer dizer que o que está registrado tem conta, não que o DRE está completo: um gasto que ainda não foi lançado nem importado não aparece aqui.'
      : 'Nada para mostrar neste filtro.';

  return (
    <section aria-labelledby="classification-title" className="min-w-0">
      <PageHeader
        trail="Contabilidade gerencial / Classificar lançamentos"
        id="classification-title"
        title="Classificar lançamentos"
        description="Diga de que é cada dinheiro que entrou ou saiu. Conciliar diz o que um pagamento quitou; classificar diz se é despesa ou receita, e de quê."
      />
      <div className="grid gap-4">
        <SegmentedControl
          label="Origem dos lançamentos"
          tabs
          value={view.kind}
          onChange={(kind) => {
            setSelected(new Set());
            onView({ kind });
          }}
          options={KINDS.map((kind) => ({ value: kind.value, label: kind.label }))}
        />
        <div>
          <SummaryStrip label="Resumo da classificação" items={summaryItems} />
          <p className="-mt-2 text-xs text-content-muted">
            O percentual classificado mede quanto do que está registrado já tem conta. Não prova que
            tudo foi registrado nem que o mês está certo.
          </p>
        </div>
        <FilterBar
          searchId="classification-search"
          searchLabel="Buscar lançamento"
          searchPlaceholder="Descrição, conta ou contraparte"
          search={view.search}
          onSearchChange={(search) => onView({ search }, { replace: true })}
          chips={
            view.filter === 'PENDING'
              ? []
              : [
                  {
                    key: 'filter',
                    label: FILTERS.find((one) => one.value === view.filter)?.label ?? '',
                    onRemove: () => onView({ filter: 'PENDING' }),
                  },
                ]
          }
          onClear={() => onView({ filter: 'PENDING', search: '' })}
          count={`${total} ${total === 1 ? 'lançamento' : 'lançamentos'}`}
          resultsLabel={`Ver ${total} ${total === 1 ? 'lançamento' : 'lançamentos'}`}
          pageSize={view.limit}
          onPageSizeChange={(limit) => onView({ limit })}
        >
          <FilterGroup
            label="Situação"
            description="Sem conta"
            options={[...FILTERS]}
            value={view.filter}
            onChange={(filter) => onView({ filter })}
          />
        </FilterBar>
        {selectedRows.length ? (
          <div className="flex flex-wrap items-center gap-3 rounded-xl border border-brand-500 bg-brand-500/5 px-4 py-2">
            <strong className="text-sm">
              {selectedRows.length} selecionado{selectedRows.length > 1 ? 's' : ''}
            </strong>
            <div className="min-w-0 flex-1 sm:max-w-sm">
              <Combobox
                label="Conta para os selecionados"
                hideLabel
                placeholder="Escolher conta para todos"
                selectedLabel=""
                options={options(selectedRows[0]!.direction)}
                clearable={false}
                disabled={busy}
                onSelect={pick(selectedRows)}
              />
            </div>
            <Button
              size="sm"
              variant="outline"
              disabled={busy || !targetsOf(selectedRows).some((row) => row.account)}
              onClick={() => void clear(selectedRows)}
            >
              Tirar a conta
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
              Limpar seleção
            </Button>
          </div>
        ) : null}
        {page.isPending ? (
          <LoadingCard label="Carregando a fila" />
        ) : page.isError ? (
          <ErrorCard message={getApiError(page.error)} />
        ) : rows.length === 0 ? (
          <EmptyCard
            icon={Inbox}
            title={
              view.filter === 'PENDING' && !view.search.trim()
                ? `Nada sem conta em ${KINDS.find((kind) => kind.value === view.kind)?.label} neste mês`
                : 'Nada encontrado'
            }
            description={emptyText}
          />
        ) : (
          <div className="hue-responsive-collection overflow-hidden rounded-2xl border border-surface-border bg-surface-card shadow-soft">
            <CollectionCards>
              <div className="hue-collection-cards">
                <SortBar
                  label="Ordenação da fila"
                  columns={columns}
                  sort={view.sort}
                  direction={view.direction}
                  onToggle={toggleSort}
                />
                <ul className="divide-y divide-surface-border">
                  {rows.map((row) => (
                    <li key={keyOf(row)} className="grid gap-3 px-4 py-4">
                      <div className="flex items-start gap-3">
                        {checkbox(row)}
                        <div className="min-w-0 flex-1">{describe(row)}</div>
                        {amountOf(row)}
                      </div>
                      <div className="flex flex-wrap items-center gap-3 text-xs text-content-muted">
                        {row.date ? formatCivilDate(row.date) : 'Mensal'}
                        {partOf(row)}
                      </div>
                      {accountField(row)}
                      {statusOf(row)}
                      {actionsOf(row)}
                      {row.parcels && mounted.has(keyOf(row))
                        ? drawer(
                            isOpen(row),
                            <ul
                              aria-label={`Parcelas de ${purchaseName(row.description)}`}
                              className="ml-2 grid gap-4 rounded-r-xl border-l-2 border-brand-500/40 bg-brand-500/5 py-3 pl-4 pr-3"
                            >
                              {row.parcels.map((parcel) => (
                                <li key={keyOf(parcel)} className="grid gap-2">
                                  <div className="flex items-start justify-between gap-3">
                                    <p className="text-sm text-content">
                                      {parcelName(parcel.description)}
                                    </p>
                                    {amountOf(parcel)}
                                  </div>
                                  {accountField(parcel)}
                                  {statusOf(parcel)}
                                </li>
                              ))}
                            </ul>,
                            '',
                          )
                        : null}
                    </li>
                  ))}
                </ul>
              </div>
            </CollectionCards>
            <CollectionTable>
              <table className="w-full table-fixed border-collapse text-left">
                <caption className="sr-only">Fila de classificação</caption>
                <colgroup>
                  <col className="w-10" />
                  <col className="w-[9%]" />
                  <col className="w-[29%]" />
                  <col className="w-[13%]" />
                  <col className="w-[11%]" />
                  <col className="w-[24%]" />
                  <col className="w-[14%]" />
                </colgroup>
                <thead>
                  <tr className="border-b border-surface-border">
                    <th scope="col" className="px-3">
                      <span className="sr-only">Selecionar</span>
                    </th>
                    {columns.slice(0, 2).map((column) => (
                      <SortableColumn
                        key={column.sort}
                        column={column.sort}
                        label={column.label}
                        sort={view.sort}
                        direction={view.direction}
                        onToggle={toggleSort}
                      />
                    ))}
                    <th scope="col" className="px-5 py-3 text-xs font-semibold text-content/70">
                      Parte
                    </th>
                    <SortableColumn
                      column="amount"
                      label="Valor"
                      sort={view.sort}
                      direction={view.direction}
                      onToggle={toggleSort}
                    />
                    <th scope="col" className="px-5 py-3 text-xs font-semibold text-content/70">
                      Conta do plano
                    </th>
                    <th scope="col" className="px-5 py-3 text-xs font-semibold text-content/70">
                      Classificação
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-border">
                  {rows.map((row) => (
                    <Fragment key={keyOf(row)}>
                      <tr
                        className={`align-top ${
                          row.parcels && expanded.has(keyOf(row)) ? 'bg-brand-500/5' : ''
                        }`}
                      >
                        <td className="px-3 py-4">{checkbox(row)}</td>
                        <td className="px-5 py-4 text-sm text-content-muted">
                          {row.date ? formatCivilDate(row.date) : 'Mensal'}
                        </td>
                        <td className="px-5 py-4">{describe(row)}</td>
                        <td className="px-5 py-4">{partOf(row)}</td>
                        <td className="px-5 py-4">{amountOf(row)}</td>
                        {/* As ações moram embaixo da conta: numa coluna própria, estreita, o botão
                      "Declarar sem compromisso" vazava para fora do cartão. */}
                        <td className="px-5 py-4">
                          <div className="grid gap-2">
                            {accountField(row)}
                            {actionsOf(row)}
                          </div>
                        </td>
                        <td className="px-5 py-4">{statusOf(row)}</td>
                      </tr>
                      {row.parcels && mounted.has(keyOf(row)) ? (
                        <tr className="align-top">
                          <td colSpan={7} className="p-0">
                            {drawer(
                              isOpen(row),
                              <div aria-label={`Parcelas de ${purchaseName(row.description)}`}>
                                {row.parcels.map((parcel) => (
                                  <div
                                    key={keyOf(parcel)}
                                    className={`grid ${PARCEL_COLUMNS} items-start border-t border-surface-border/60 bg-brand-500/[0.03] first:border-t-0`}
                                  >
                                    <span />
                                    <span />
                                    <div className="py-3 pl-6 pr-5">{describeParcel(parcel)}</div>
                                    <span />
                                    <div className="px-5 py-3">{amountOf(parcel)}</div>
                                    <div className="px-5 py-3">{accountField(parcel)}</div>
                                    <div className="px-5 py-3">{statusOf(parcel)}</div>
                                  </div>
                                ))}
                              </div>,
                              '',
                            )}
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </CollectionTable>
            <Pagination
              total={total}
              limit={view.limit}
              offset={view.offset}
              onOffsetChange={(offset) => onView({ offset })}
              itemLabel={(count) => (count === 1 ? 'lançamento' : 'lançamentos')}
            />
          </div>
        )}
        <p className="text-sm text-content-muted">
          {view.kind === 'MOVEMENTS'
            ? 'A parte de um movimento que pagou um compromisso não aparece aqui: ela usa a conta do compromisso, para a despesa não contar duas vezes. Se o movimento já tinha conta própria, ela fica guardada e volta a valer se sobrar valor sem conciliar.'
            : view.kind === 'OBLIGATIONS'
              ? 'A conta do compromisso vale no DRE do mês de competência dele, pago ou não, e vai com o pagamento para o Fluxo de caixa.'
              : 'Trocar a conta da recorrência vale dos próximos meses gerados em diante. Os meses já gerados mudam um a um, em Compromissos.'}
        </p>

        <Modal
          open={confirming !== null}
          onOpenChange={(open) => (open ? undefined : setConfirming(null))}
          title="Trocar a conta de parcelas já classificadas?"
          footer={
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setConfirming(null)}>
                Cancelar
              </Button>
              <Button
                disabled={busy}
                onClick={() =>
                  confirming ? void classify(confirming.rows, confirming.account, true) : undefined
                }
              >
                Trocar e classificar
              </Button>
            </div>
          }
        >
          {confirming ? (
            <div className="grid gap-3 text-sm">
              <p>
                Todas as parcelas vão para {confirming.account.code} {confirming.account.name}.
                Estas já tinham outra conta e vão mudar:
              </p>
              <ul className="grid gap-1 text-content-muted">
                {confirming.changes.map((parcel) => (
                  <li key={keyOf(parcel)}>
                    {parcel.description} · hoje em {parcel.account?.code} {parcel.account?.name}
                  </li>
                ))}
              </ul>
              <p className="text-content-muted">
                Contas diferentes podem estar certas, como juros numa conta e o valor principal em
                outra. Se for o caso, cancele e classifique cada parcela abrindo a compra.
              </p>
            </div>
          ) : null}
        </Modal>

        <Modal
          open={declaring !== null}
          onOpenChange={(open) => (open ? undefined : setDeclaring(null))}
          title="Declarar sem compromisso"
          {...(declaring
            ? {
                description: `${declaring.row.description} · ${formatCurrency(declaring.row.amountCents ?? 0, 'BRL')}`,
              }
            : {})}
          footer={
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setDeclaring(null)}>
                Cancelar
              </Button>
              <Button type="submit" form="declaration-form" disabled={busy}>
                Declarar
              </Button>
            </div>
          }
        >
          {declaring ? (
            <form
              id="declaration-form"
              onSubmit={(event) => void submitDeclaration(event)}
              className="grid gap-4"
            >
              <p className="text-sm text-content-muted">
                São duas respostas diferentes. <strong>Declarar</strong> diz que este dinheiro nunca
                vai pagar um compromisso. <strong>A conta</strong> diz de que é a despesa ou
                receita. Com as duas, o movimento passa a valer no DRE.
              </p>
              <Input
                label="Motivo"
                placeholder="Ex.: corrida para levar material"
                value={declaring.reason}
                error={declaring.errors.reason}
                onChange={(event) => setDeclaring({ ...declaring, reason: event.target.value })}
              />
              <MonthPicker
                label="Mês de competência"
                variant="field"
                clearable={false}
                value={declaring.month}
                hint="Já vem o mês do caixa. Troque se a despesa pertence a outro mês."
                onValueChange={(month) =>
                  setDeclaring({ ...declaring, month: month ?? declaring.month })
                }
              />
              <Combobox
                label="Conta do plano (opcional)"
                placeholder="Escolher conta"
                selectedLabel={(() => {
                  const chosen = plan.find((account) => account.id === declaring.accountId);
                  return chosen ? `${chosen.code} ${chosen.name}` : '';
                })()}
                options={options(declaring.row.direction)}
                onClear={() => setDeclaring({ ...declaring, accountId: null })}
                onSelect={(choice) =>
                  choice.kind === 'option'
                    ? setDeclaring({ ...declaring, accountId: choice.value })
                    : undefined
                }
              />
              {declaring.errors.form ? <RequestAlert message={declaring.errors.form} /> : null}
            </form>
          ) : null}
        </Modal>

        <Modal
          open={competence !== null}
          onOpenChange={(open) => (open ? undefined : setCompetence(null))}
          title="Mês de competência"
          {...(competence ? { description: competence.row.description } : {})}
          footer={
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setCompetence(null)}>
                Cancelar
              </Button>
              <Button type="submit" form="competence-form" disabled={busy}>
                Salvar
              </Button>
            </div>
          }
        >
          {competence ? (
            <form
              id="competence-form"
              onSubmit={(event) => void submitCompetence(event)}
              className="grid gap-3"
            >
              <p className="text-sm text-content-muted">
                O mês em que esta despesa ou receita conta no DRE. O mês do caixa não muda.
              </p>
              <MonthPicker
                label="Mês de competência"
                variant="field"
                clearable={false}
                value={competence.month}
                onValueChange={(month) =>
                  setCompetence({ ...competence, month: month ?? competence.month })
                }
              />
            </form>
          ) : null}
        </Modal>
        {closedPeriod.dialog}
      </div>
    </section>
  );
}
